import React, { useState, useEffect } from 'react';
import { getTopicsBySubject, saveExamTopicDetails, getExamTopicDetails } from '../services/topicService';

// Maximum question limits per subject
const SUBJECT_LIMITS = {
  'Türkçe': 40,
  'Matematik': 40,
  'Fen Bilimleri': 20,
  'Sosyal Bilimler': 20,
  'Fizik': 14,
  'Kimya': 13,
  'Biyoloji': 13,
  'Tarih': 5,
  'Coğrafya': 5,
  'Felsefe': 5,
  'Din': 5,
  'Din Kültürü': 5
};

const TopicDetailModal = ({ isOpen, onClose, examId, subject, subjectLabel, onSave }) => {
  const [topics, setTopics] = useState([]);
  const [topicResults, setTopicResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [maxQuestions, setMaxQuestions] = useState(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTopics([]);
      setTopicResults({});
      setError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && subject) {
      loadTopicsAndData();
    }
  }, [isOpen, subject, examId]);

  const loadTopicsAndData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Set maximum questions for this subject
      setMaxQuestions(SUBJECT_LIMITS[subject] || null);
      
      // First, load topics
      const response = await getTopicsBySubject(subject);
      setTopics(response.topics || []);
      
      // Initialize empty results for all topics
      const initialResults = {};
      response.topics.forEach(topic => {
        initialResults[topic.id] = {
          topicId: topic.id,
          topicName: topic.name,
          correct: 0,
          wrong: 0,
          empty: 0,
          total: 0
        };
      });
      
      // If examId exists, try to load existing data
      if (examId) {
        try {
          const existingResponse = await getExamTopicDetails(examId, subject);
          if (existingResponse.success && existingResponse.topicDetail) {
            // Merge existing data with initial results
            existingResponse.topicDetail.topicResults.forEach(result => {
              if (initialResults[result.topicId]) {
                initialResults[result.topicId] = {
                  ...initialResults[result.topicId],
                  ...result
                };
              }
            });
          }
        } catch (err) {
          // No existing data, that's OK - use initial results
          console.log('No existing topic data found');
        }
      }
      
      setTopicResults(initialResults);
    } catch (err) {
      setError('Konular yüklenirken hata oluştu');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (topicId, field, value) => {
    const numValue = parseInt(value) || 0;
    setTopicResults(prev => {
      const current = prev[topicId];
      const { correct = 0, wrong = 0, total = 0 } = current;
      
      let updatedResult = {
        ...current,
        [field]: numValue
      };
      
      // Auto-calculate empty based on total, correct, wrong
      if (field === 'correct' || field === 'wrong') {
        const newCorrect = field === 'correct' ? numValue : correct;
        const newWrong = field === 'wrong' ? numValue : wrong;
        updatedResult.empty = Math.max(0, total - newCorrect - newWrong);
      }
      
      return {
        ...prev,
        [topicId]: updatedResult
      };
    });
  };

  const handleTotalChange = (topicId, value) => {
    const numValue = parseInt(value) || 0;
    setTopicResults(prev => {
      const current = prev[topicId];
      const { correct = 0, wrong = 0 } = current;
      
      return {
        ...prev,
        [topicId]: {
          ...current,
          total: numValue,
          // Auto-calculate empty when total changes
          empty: Math.max(0, numValue - correct - wrong)
        }
      };
    });
  };

  const calculateNet = (correct, wrong) => {
    return (correct - (wrong / 4)).toFixed(2);
  };

  const getTotalNet = () => {
    return Object.values(topicResults).reduce((sum, topic) => {
      return sum + parseFloat(calculateNet(topic.correct, topic.wrong));
    }, 0).toFixed(2);
  };

  const getTotalStats = () => {
    return Object.values(topicResults).reduce((stats, topic) => {
      return {
        correct: stats.correct + (topic.correct || 0),
        wrong: stats.wrong + (topic.wrong || 0),
        empty: stats.empty + (topic.empty || 0),
        total: stats.total + (topic.total || 0)
      };
    }, { correct: 0, wrong: 0, empty: 0, total: 0 });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate
      const resultsArray = Object.values(topicResults).filter(r => r.total > 0);
      
      if (resultsArray.length === 0) {
        setError('En az bir konu için soru sayısı giriniz');
        return;
      }

      // Check total questions limit
      const totalStats = getTotalStats();
      if (maxQuestions && totalStats.total > maxQuestions) {
        setError(`Toplam soru sayısı ${maxQuestions}'i geçemez! (Mevcut: ${totalStats.total})`);
        return;
      }

      // Check if sum matches total
      for (const result of resultsArray) {
        const sum = result.correct + result.wrong + result.empty;
        if (sum !== result.total) {
          setError(`${result.topicName}: Doğru + Yanlış + Boş = Toplam olmalı (${sum} ≠ ${result.total})`);
          return;
        }
      }

      if (examId) {
        // Save to database
        await saveExamTopicDetails(examId, subject, resultsArray);
      }
      
      // Call parent callback
      if (onSave) {
        onSave(subject, resultsArray);
      }

      onClose();
    } catch (err) {
      setError(err.message || 'Kaydedilirken hata oluştu');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">
            {subjectLabel} - Konu Detayları
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Sticky Total Stats Display */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-300 rounded-lg p-4 shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Total Stats */}
                <div className={`text-center p-2 rounded ${
                  maxQuestions && getTotalStats().total > maxQuestions 
                    ? 'bg-red-50 border-2 border-red-300' 
                    : 'bg-white bg-opacity-50'
                }`}>
                  <p className="text-xs text-gray-600 font-medium mb-1">Toplam Soru</p>
                  <p className={`text-lg font-bold ${
                    maxQuestions && getTotalStats().total > maxQuestions 
                      ? 'text-red-600' 
                      : 'text-gray-800'
                  }`}>
                    {getTotalStats().total}{maxQuestions ? ` / ${maxQuestions}` : ''}
                  </p>
                  {maxQuestions && getTotalStats().total > maxQuestions && (
                    <p className="text-xs text-red-600 font-semibold mt-1">Limit aşıldı!</p>
                  )}
                </div>
                <div className="text-center p-2 bg-green-50 bg-opacity-70 rounded">
                  <p className="text-xs text-green-700 font-medium mb-1">Doğru</p>
                  <p className="text-lg font-bold text-green-600">{getTotalStats().correct}</p>
                </div>
                <div className="text-center p-2 bg-red-50 bg-opacity-70 rounded">
                  <p className="text-xs text-red-700 font-medium mb-1">Yanlış</p>
                  <p className="text-lg font-bold text-red-600">{getTotalStats().wrong}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 bg-opacity-70 rounded">
                  <p className="text-xs text-gray-700 font-medium mb-1">Boş</p>
                  <p className="text-lg font-bold text-gray-600">{getTotalStats().empty}</p>
                </div>
                {/* Total Net - Highlighted */}
                <div className="col-span-2 md:col-span-1 text-center p-2 bg-primary-500 rounded">
                  <p className="text-xs text-white font-medium mb-1">Net</p>
                  <p className="text-2xl font-bold text-white">{getTotalNet()}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-4">
                <p className="text-error-700">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Topics Grid */}
                <div className="grid gap-4">
                {topics.map((topic) => {
                  const result = topicResults[topic.id] || {};
                  const { correct = 0, wrong = 0, empty = 0, total = 0 } = result;
                  const net = calculateNet(correct, wrong);
                  
                  return (
                    <div
                      key={topic.id}
                      className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary-300 transition"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {topic.order}. {topic.name}
                        </h3>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Net</p>
                          <p className="text-xl font-bold text-primary-600">{net}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Toplam
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={total}
                            onChange={(e) => handleTotalChange(topic.id, e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Doğru
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={total}
                            value={correct}
                            onChange={(e) => handleInputChange(topic.id, 'correct', e.target.value)}
                            disabled={total === 0}
                            className="w-full px-2 py-1.5 text-sm border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Yanlış
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={total}
                            value={wrong}
                            onChange={(e) => handleInputChange(topic.id, 'wrong', e.target.value)}
                            disabled={total === 0}
                            className="w-full px-2 py-1.5 text-sm border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Boş
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={total}
                            value={empty}
                            disabled={true}
                            className="w-full px-2 py-1.5 text-sm border-2 border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-600"
                            placeholder="Auto"
                          />
                        </div>
                      </div>

                      {/* Validation indicator */}
                      {total > 0 && correct + wrong + empty !== total && (
                        <p className="text-xs text-red-600 mt-2">
                          ⚠️ Toplam eşleşmiyor: {correct} + {wrong} + {empty} = {correct + wrong + empty} (olmalı: {total})
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kaydediliyor...
              </span>
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicDetailModal;
